import { readSessionActor } from "@/lib/analytics/actor";
import {
  hasDefaultShippingAddress,
  isGatedActor
} from "@/lib/first-login/gate";
import { addressesQuery } from "@/queries/customer/addresses";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

/**
 * Returns a function answering "must the buyer give a delivery address before
 * we send them to the supplier's checkout?".
 *
 * The rule is the first-login gate's: a salon with no default shipping address,
 * because the Shopify prefill is built server-side from that address. Operators
 * and unknown actors are never asked (`isGatedActor`), and an address list that
 * cannot be read answers "no" - the same fail-open stance as the gate, since
 * blocking checkout on a transient API error is worse than sending the buyer to
 * the supplier without a prefill.
 *
 * The list is read with `fetchQuery` at click time. It honours the query's
 * stale time, and saving an address invalidates it, so a buyer who has just
 * saved one is not asked again from a stale empty list.
 */
export function useNeedsExternalCheckoutAddress() {
  const queryClient = useQueryClient();

  return useCallback(async (): Promise<boolean> => {
    // Nothing to read for an actor the gate would never ask.
    if (!isGatedActor(readSessionActor())) return false;

    try {
      const { addresses } = await queryClient.fetchQuery(addressesQuery());
      return !hasDefaultShippingAddress(addresses);
    } catch {
      return false;
    }
  }, [queryClient]);
}
